import React, { useEffect, useCallback } from "react";

export function LightboxModal({ isOpen, images = [], currentIndex = 0, onClose, onPrev, onNext }) {
  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    },
    [isOpen, onClose, onPrev, onNext]
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || images.length === 0) return null;

  const currentImg = images[currentIndex];
  const imgSrc = typeof currentImg === "string" ? currentImg : currentImg?.src || currentImg?.url;
  const imgAlt = typeof currentImg === "string" ? "Galleri billede" : currentImg?.alt || "Galleri billede";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-3 text-white hover:text-[#c06228] bg-white/10 hover:bg-white/20 rounded-full transition-all focus:outline-none"
        aria-label="Luk galleri"
      >
        <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
      </button>

      {/* Counter indicator */}
      <div className="absolute top-5 left-6 z-50 text-white/80 text-sm font-mono bg-black/50 px-3 py-1 rounded">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Main image container */}
      <div
        className="relative max-w-5xl max-h-[85vh] w-full h-full flex items-center justify-center p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imgSrc}
          alt={imgAlt}
          loading="lazy"
          className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded shadow-2xl transition-all duration-300"
        />
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-4 text-white hover:text-[#c06228] bg-black/40 hover:bg-black/80 rounded-full transition-colors focus:outline-none"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-2xl" aria-hidden="true" />
        </button>
      )}

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-4 text-white hover:text-[#c06228] bg-black/40 hover:bg-black/80 rounded-full transition-colors focus:outline-none"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-2xl" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
